<template>
  <VcModalContainer
    show-dialog
    @input="closeDialog"
    size="md"
  >
    <template #content>
      <VcLayout align-center justify-start column fill-height class="client-package-history-dialog">
        <VcIcon right class="close-icon" @click="closeDialog">close</VcIcon>

        <VcLayout column class="dialog-content-wrapper">
          <VcLayout column class="dialog-header flex-grow-0">
            <div class="dialog-title">
              {{ $t('payment.packages.client_package_page.package_history.title') }}
            </div>
            <div class="dialog-subtitle">
              {{ $t('payment.packages.client_package_page.package_history.subtitle', {date: packageStartTimeData}) }}
            </div>
          </VcLayout>

          <VcLayout column class="usage-list flex-grow-0" v-if="packageUsage">
            <VcLayout class="usage-header">
              <div>{{ $t('payment.packages.client_package_page.package_history.time_header') }}</div>
              <div>{{ $t('payment.packages.client_package_page.package_history.service_name_header') }}</div>
            </VcLayout>
            <div class="usage-items-wrapper" v-if="packageUsage.length">
              <VcLayout v-for="(usage, idx) in packageUsage" class="usage-item"
                        :data-qa="`usage-item-${idx}`"
                        @click="goToBooking(usage)">
                <div class="usage-item-date" :data-qa="`usage-${idx}-date`">
                  {{ usage.start_time | dateFormat(localeDateAndTimeFormat, staff.time_zone_offset) }}
                </div>
                <div class="usage-item-title" :data-qa="`usage-${idx}-title`"
                     v-text-truncate="{ maxLines: serviceNameMaxLines, expandable: true }">
                  {{ usage.title }}
                </div>
              </VcLayout>
            </div>
            <div v-else class="usage-empty-state">
              {{ $t('payment.packages.client_package_page.package_history.empty_state') }}
            </div>
          </VcLayout>
          <template v-else-if="hasError">
            <span class="error--text">{{ $t('common.actions.try_again') }}</span>
          </template>
          <VcLayout align-center justify-center class="loader-container ma-4" v-else>
            <VcLoader/>
          </VcLayout>
        </VcLayout>
      </VcLayout>
    </template>
  </VcModalContainer>
</template>
<script>
import packagesService from "@/modules/packages/services/packagesService";
import { mapGetters } from "vuex";
import { FORMATS } from "@/modules/common/filters/DateFormats";

export default {
  components: {},
  props: {
    clientUid: {
      type: String,
      required: true
    },
    conversationUid: {
      type: String,
      required: true
    },
    clientPackageUid: {
      type: String,
      required: true
    },
    packageStartTime: {
      type: Date | String,
      required: true
    }
  },
  data() {
    return {
      packageUsage: null,
      hasError: false,
    };
  },
  created() {
    this.fetchPackageUsage();
  },
  methods: {
    async fetchPackageUsage() {
      try {
        const data = await packagesService.getPackageUsage(this.clientUid, this.conversationUid, this.clientPackageUid);
        this.packageUsage = data;
      } catch (e) {
        this.hasError = true;
      }
    },
    closeDialog() {
      this.$emit("close");
    },
    goToBooking(booking) {
      this.closeDialog();
      const route = booking.booking_type === 'event' ? 'app.event' : 'app.appointment.show';
      this.$frontageApi.navigateTo(route, {uid: booking.booking_uid});
    }
  },
  computed: {
    ...mapGetters('accountModule', ['business', 'account', 'staff']),
    localeDateFormat() {
      return this.business.locale === 'en' ? FORMATS.MONTH_DAY_YEAR : FORMATS.DAY_MONTH_YEAR;
    },
    localeDateAndTimeFormat() {
      const timeFormat = this.account.settings.time_format === '24h' ? 'HH:mm' : 'h:mma';
      return `${this.localeDateFormat}, ${timeFormat}`;
    },
    packageStartTimeData() {
      return this.$options.filters.dateFormat(this.packageStartTime, this.localeDateFormat);
    },
    serviceNameMaxLines() {
      return this.$vuetify.breakpoint.smAndDown ? 2 : 1;
    }
  }
}
</script>
<style lang="scss">
@import "@/assets/scss/abstract/_variables.scss";
@import "@/assets/scss/abstract/_mixins.scss";

.v-dialog:has(.client-package-history-dialog) {
  left: 0 !important;
}

.client-package-history-dialog {
  max-height: 438px;

  .close-icon {
    position: absolute;
    top: 20px;
    @include i-position-mirror(right, 20px);
    z-index: 5;
  }

  .dialog-content-wrapper {
    width: 100%;
    overflow: hidden;
    padding: 16px;
    padding-bottom: 0;
    gap: 16px;

    .dialog-header {
      gap: 4px;

      .dialog-title {
        font-weight: 500;
        font-size: 20px;
        line-height: 30px;
      }

      .dialog-subtitle {
        font-weight: 400;
        font-size: 12px;
        line-height: 16px;
        color: #616161;
      }
    }

    .usage-list {
      overflow: hidden;
      border: 1px solid #EBEBEB;
      border-radius: 8px;

      .usage-header {
        padding: 8px 16px;
        font-weight: 400;
        font-size: 14px;
        line-height: 18px;
        color: #757575;
        border-bottom: 1px solid #EBEBEB;
      }

      .usage-items-wrapper {
        overflow: auto;

        .usage-item {
          padding: 12px 16px;
          font-size: 14px;
          line-height: 22px;
          cursor: pointer;

          &:not(:last-child) {
            border-bottom: 1px solid #EBEBEB;
          }

          &:hover {
            background-color: #f0f0f0;
          }

          .usage-item-date {
            font-weight: 500;
          }

          .usage-item-title {
            font-weight: 400;
          }
        }
      }

      .usage-header,
      .usage-item {
        gap: 8px;

        & > :first-child {
          flex: 0 0 35%;
        }

        & > :last-child {
          flex: 0 0 65%;
        }

        @include sm-and-down {
          & > :first-child {
            flex: 0 0 40%;
            width: 40%;
          }

          & > :last-child {
            flex: 0 0 60%;
            width: 60%;
          }
        }
      }

      .usage-empty-state {
        padding: 12px 16px;
        font-weight: 500;
        font-size: 14px;
        line-height: 24px;
      }
    }

    .loader-container {
      width: 32px;
      height: 32px;
      position: relative;
      align-self: center;
    }
  }
}
</style>
